/**
 * EditApiKeyRolesModal Component
 *
 * Replaces the roles of an API key's identity through `updateApiKeyRoles`.
 */
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { FormModal, InputWrapper, Modal, Select } from '@insio/ui-toolkit';
import { UpdateApiKeyRolesDocument, type UserRole } from '@/graphql';
import { KEY_ASSIGNABLE_ROLES, isNonAssignableRole } from '@/features/admin/constants/roles';
import { useRoleLabelOptions } from '@/features/admin/hooks/use-role-label-options';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

interface EditApiKeyRolesModalProps {
  apiKey: { id: string; name: string; roles: readonly UserRole[] };
  onClose: () => void;
  onSuccess: () => void;
}

const isKeyAssignable = (role: UserRole) => KEY_ASSIGNABLE_ROLES.includes(role);

export function EditApiKeyRolesModal({ apiKey, onClose, onSuccess }: EditApiKeyRolesModalProps) {
  const t = useFeatureTranslation('systemSettings.apiKeys');
  const tRole = useEnumTranslation('userRole');
  const roleOptions = useRoleLabelOptions(KEY_ASSIGNABLE_ROLES);
  const [roles, setRoles] = useState<UserRole[]>(() => apiKey.roles.filter(isKeyAssignable));
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [updateApiKeyRoles, { loading }] = useMutation(UpdateApiKeyRolesDocument);

  // Non-assignable roles survive the save; any other role a key may not hold (legacy ADMIN) is
  // dropped.
  const removedRoles = apiKey.roles.filter(
    role => !isKeyAssignable(role) && !isNonAssignableRole(role)
  );

  const handleRolesChange = (value: string | number | (string | number)[]) => {
    const selected = (Array.isArray(value) ? value : [value]).map(String);
    setRoles(KEY_ASSIGNABLE_ROLES.filter(role => selected.includes(role)));
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    try {
      const result = await updateApiKeyRoles({
        variables: { input: { id: apiKey.id, roles } }
      });
      if (result.error) {
        setSubmitError(result.error.message);
        return;
      }
      if (result.data?.updateApiKeyRoles) {
        onSuccess();
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('errors.updateRolesError'));
    }
  };

  return (
    <FormModal
      open
      testId='edit-api-key-roles-modal'
      title={t('editRoles.title')}
      description={t('editRoles.description', { name: apiKey.name })}
      submitLabel={loading ? t('editRoles.saving') : t('editRoles.save')}
      cancelLabel={t('actions.cancel')}
      loading={loading}
      error={submitError}
      onSubmit={handleSubmit}
      onClose={onClose}
    >
      {removedRoles.length > 0 && (
        <Modal.Warning testId='edit-api-key-roles-legacy-note'>
          {t('editRoles.legacyRolesNote', {
            roles: removedRoles.map(role => tRole(role)).join(', ')
          })}
        </Modal.Warning>
      )}

      <InputWrapper
        label={t('fields.roles')}
        htmlFor='api-key-roles'
        labelSize='sm'
        className='min-h-96'
      >
        <Select
          id='api-key-roles'
          testId='edit-api-key-roles-select'
          isMulti
          value={roles}
          onChange={handleRolesChange}
          options={roleOptions}
          disabled={loading}
        />
      </InputWrapper>
    </FormModal>
  );
}
